export type TypeGuard<T extends ARG, ARG = unknown> = (v: ARG) => v is T;

export function isEither<T1, T2>(t1: TypeGuard<T1>, t2: TypeGuard<T2>, obj: unknown): obj is T1 | T2 {
    return t1(obj) || t2(obj);
}

export function isEither_F<T1, T2>(t1: TypeGuard<T1>, t2: TypeGuard<T2>): (obj: unknown) => obj is T1 | T2 {
    return (obj: unknown): obj is T1 | T2 => t1(obj) || t2(obj);
}

export function isNull(input: unknown): input is null {
    return input === null;
}

export function isNotNull<T>(input: null | undefined | T): input is T {
    return input != null;
}

export function isOptionalString(s: unknown): s is string | undefined {
    return isOptional(isString, s);
}

export function isOptionalNumber(s: unknown): s is string | undefined {
    return isOptional(isNumber, s);
}

export function isOptionalBoolean(s: unknown): s is boolean | undefined {
    return isOptional(isBoolean, s);
}

export function isArrayOf<T>(typeGuard: TypeGuard<T>, a: unknown): a is T[] {
    return Array.isArray(a) && a.every(typeGuard);
}

export function isOnepleOf<T>(type: TypeGuard<T>, obj: unknown): obj is [T] {
    return Array.isArray(obj) && obj.length === 1 && type(obj[0]);
}

export function isOnepleOf_F<T>(type: TypeGuard<T>): (obj: unknown) => obj is [T] {
    return (obj: unknown): obj is [T] => Array.isArray(obj) && obj.length === 1 && type(obj[0]);
}

export function isArrayOf_F<T>(typeGuard: TypeGuard<T>): (a: unknown) => a is T[] {
    return (a: unknown): a is T[] => isArrayOf(typeGuard, a);
}

export type ElementType<T extends unknown[]> = T extends (infer U)[] ? U : never;

export function isNullable<T>(typeGuard: TypeGuard<T>, x: unknown): x is T | null {
    return x === null || typeGuard(x);
}

export function isOptional<T>(typeGuard: TypeGuard<T>, x: unknown): x is T | undefined {
    return typeof x === 'undefined' || typeGuard(x);
}
export function isOptional_F<T>(typeGuard: TypeGuard<T>): (x: unknown) => x is T | undefined {
    return (x: unknown): x is T | undefined => typeof x === 'undefined' || typeGuard(x);
}

export function isOptionalOrNull<T>(typeGuard: TypeGuard<T>, x: unknown): x is T | undefined {
    return x === null || typeof x === 'undefined' || typeGuard(x);
}

export function isOptionalOrNull_F<T>(typeGuard: TypeGuard<T>): (x: any) => x is T | undefined {
    return (x: any): x is T | undefined => isOptionalOrNull(typeGuard, x);
}

export function isDefined<T>(x: T | undefined): x is T {
    return x !== undefined;
}

export function isDefinedAndNotNull<T>(x: null | undefined | T): x is T {
    return x !== undefined && x !== null;
}

export function isUndefined(x: unknown): x is undefined {
    return x === undefined;
}

// allow a type that is indexable by string to make typeguards easier
export function isObjectLike(x: unknown): x is Record<string, unknown> {
    return x !== null && typeof x === 'object';
}

export function isString(s: unknown): s is string {
    return typeof s === 'string';
}

export function isNonEmptyString(s: unknown): s is string {
    return typeof s === 'string' && s.length > 0;
}

export function isNumber(n: unknown): n is number {
    return typeof n === 'number';
}

export function isBigInt(n: unknown): n is bigint {
    return typeof n === 'bigint';
}

export function isPromise<T extends object>(obj: T | Promise<T>): obj is Promise<T> {
    return 'then' in obj && typeof obj.then === 'function';
}

export function isValidNumber(n: unknown): n is number {
    return typeof n === 'number' && !isNaN(n);
}

export function isBoolean(b: unknown): b is boolean {
    return typeof b === 'boolean';
}

export function isBooleanString(b: unknown): b is 'true' | 'false' {
    return b === 'true' || b === 'false';
}

export function isTrue(b: unknown): b is true {
    return b === true;
}

export function isFalse(b: unknown): b is false {
    return b === false;
}

export function isNumberBoolean(b: unknown): b is 0 | 1 {
    return b === 0 || b === 1;
}

export function isPrimitive(x: unknown): x is string | number | boolean {
    return typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean';
}

export function isOptionalArrayOf<T>(typeguard: TypeGuard<T>, a: unknown): a is T[] | undefined {
    return typeof a === 'undefined' || isArrayOf<T>(typeguard, a);
}

export function isOr_F<T, U>(typeGuard: TypeGuard<T>, orElse: TypeGuard<U>): (x: unknown) => x is T | U {
    return (x: unknown): x is T | U => orElse(x) || typeGuard(x);
}

export function isFunction(f: unknown): f is (...args: unknown[]) => unknown {
    return typeof f === 'function';
}

export function isDate(date: unknown): date is Date {
    return date instanceof Date;
}

export function isDateString(s: unknown): s is string {
    return isString(s) && !isNaN(Date.parse(s));
}

export function isRecord<K extends string | number | symbol, V>(isKey: TypeGuard<K>, isValue: TypeGuard<V>): (record: unknown) => record is Record<K, V> {
    return (record: unknown): record is Record<K, V> => isObjectLike(record) && Object.keys(record).every(isKey) && Object.values(record).every(isValue);
}

export function isEnumOfType<T>(enumType: T, obj: unknown): obj is T {
    if (typeof obj === 'undefined') {
        return false;
    }
    for (const key in enumType) {
        // an enum with integer values will have the number values as keys as well - so omit anything castable to a number
        if (isNaN(+key)) {
            if (obj === enumType[key]) {
                return true;
            }
        }
    }
    return false;
}

export function isEnumOfType_F<T>(enumType: T): TypeGuard<T> {
    return (obj: unknown): obj is T => isEnumOfType(enumType, obj);
}

export type Omit<T, Keys extends keyof T> = Pick<T, Exclude<keyof T, Keys>>;

export type RequireOnlyOne<T, Keys extends keyof T = keyof T> = Omit<T, Keys> & { [K in Keys]-?: Required<Pick<T, K>> & Partial<Record<Exclude<Keys, K>, undefined>> }[Keys];

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Omit<T, Keys> & { [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>> }[Keys];

export type RequiredField<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>> & { [P in K]-?: Exclude<T[P], undefined> };
export function hasRequiredField<T, K extends keyof T>(o: T | RequiredField<T, K>, field: K): o is RequiredField<T, K> {
    return !!o[field];
}

export function hasRequiredField_F<T, K extends keyof T>(field: K): (o: T | RequiredField<T, K>) => o is RequiredField<T, K> {
    return (o: T | RequiredField<T, K>): o is RequiredField<T, K> => !!o[field];
}

export function hasFieldOfType<Name extends string, Type>(obj: any, name: Name, typeGuard: TypeGuard<Type>): obj is { [key in typeof name]: Type } {
    return isDefinedAndNotNull(obj) && typeGuard(obj[name]);
}

export function hasFieldOfType_F<Name extends string, Type>(name: Name, typeGuard: TypeGuard<Type>): TypeGuard<{ [key in typeof name]: Type }> {
    return (obj: any): obj is { [key in typeof name]: Type } => {
        return isDefinedAndNotNull(obj) && typeGuard(obj[name]);
    };
}

export type Overwrite<T1, T2> = {
    [P in Exclude<keyof T1, keyof T2>]: T1[P];
} & T2;

export type MarkPartial<T, K extends keyof T> = Omit<T, K> & { [Key in K]: Partial<T[Key]> };

export function or<T1, T2>(t1: TypeGuard<T1>, t2: TypeGuard<T2>): (obj: any) => obj is T1 | T2 {
    return (obj: any): obj is T1 | T2 => t1(obj) || t2(obj);
}

type _Combine<T, K extends PropertyKey = T extends unknown ? keyof T : never> = T extends unknown ? T & Partial<Record<Exclude<K, keyof T>, never>> : never;

// combines the fields of a union of types into 1 type
export type Combine<T> = { [K in keyof _Combine<T>]-?: _Combine<T>[K] };

export function enumify<T extends string>(values: readonly T[]): { readonly [K in T]: K } {
    return Object.fromEntries(values.map(x => [x, x] as const)) as {
        readonly [K in T]: K;
    };
}

export type KeysOfType<O, T> = {
    [K in keyof O]: O[K] extends T ? K : never;
}[keyof O];

export type RequireAtLeastOneElement<T> = [T, ...T[]];
